// src/stores/serviceCasesStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { auth, db } from '@/services/firebaseconfig'
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { subscribeOnce } from '@/utils/subscriptionRegistry'
import { sanitizeVisitDate } from '@/utils/visitDate'
import { requireHomeAccess } from '@/utils/deriveDispatchPlan'
import { changeAlertSuppression } from '@/composables/usePowerHubAlertSuppression'

const ACTIVE_STATUSES = new Set(['Open', 'In Progress', 'Waiting', 'Resolved'])

function currentActor() {
  const user = auth.currentUser
  if (!user?.uid) throw new Error('Sign in again before changing a service case.')
  return { updatedByUid: user.uid, updatedByEmail: user.email || null }
}

function normalizeVisit(visit) {
  const v = visit && typeof visit === 'object' ? visit : {}
  return {
    // sanitizeVisitDate nulls implausible placeholders (0001-01-01 / Invalid) on write so a
    // date-less time can't masquerade as a booked visit (bug IyEyoBbQu8r8S3kwRDNG). Existing
    // bad docs self-heal the next time the case is saved through the store.
    scheduledStart: sanitizeVisitDate(v.scheduledStart),
    scheduledEnd: sanitizeVisitDate(v.scheduledEnd),
    subcontractorId: v.subcontractorId || null,
    subcontractorName: v.subcontractorName || null,
    workerIds: Array.isArray(v.workerIds) ? v.workerIds : [],
    notes: v.notes || null,
    calendarEventId: v.calendarEventId || null,
    // Dynamic Scheduling — estimated visit length set at creation (staff or
    // Vesta); the serviceCase appointment profile reads it via
    // durationSource 'serviceCase'.
    estimatedMinutes:
      Number.isFinite(Number(v.estimatedMinutes)) && Number(v.estimatedMinutes) > 0
        ? Number(v.estimatedMinutes)
        : null,
  }
}

export const useServiceCasesStore = defineStore('serviceCasesStore', () => {
  const activeCases = ref([]) // real-time active cases (for bell + overlays)
  const recentCases = ref([]) // recent cases (for Service Cases page list)
  const loadingActive = ref(false)
  const loadingRecent = ref(false)

  // Under the hood these are "release" fns from the registry (not raw onSnapshot unsubs).
  let unsubActive = null
  let unsubRecent = null

  const activeCount = computed(() => activeCases.value.length)

  const activeByCustomerId = computed(() => {
    const map = new Map()
    for (const sc of activeCases.value) {
      if (!sc?.customerId) continue
      if (!map.has(sc.customerId)) map.set(sc.customerId, [])
      map.get(sc.customerId).push(sc)
    }
    return map
  })

  function subscribeActive({ organizationId = null } = {}) {
    if (unsubActive) unsubActive()
    unsubActive = null

    loadingActive.value = true

    const base = collection(db, 'serviceCases')
    const clauses = [where('isActive', '==', true), orderBy('openedAt', 'desc'), limit(500)]

    if (organizationId) clauses.unshift(where('organizationId', '==', organizationId))

    const key = `serviceCases:active:${organizationId || 'all'}`

    // Dedupe across the entire app: multiple pages can "subscribe" but share one listener.
    unsubActive = subscribeOnce(key, () =>
      onSnapshot(
        query(base, ...clauses),
        snap => {
          const rows = []
          snap.forEach(d => rows.push({ id: d.id, ...d.data() }))
          activeCases.value = rows
          loadingActive.value = false
        },
        err => {
          console.error('[serviceCasesStore] active subscribe failed:', err)
          activeCases.value = []
          loadingActive.value = false
        }
      )
    )
  }

  function subscribeRecent({ organizationId = null } = {}) {
    if (unsubRecent) unsubRecent()
    unsubRecent = null

    loadingRecent.value = true

    const base = collection(db, 'serviceCases')
    const clauses = [orderBy('lastActivityAt', 'desc'), limit(500)]

    if (organizationId) clauses.unshift(where('organizationId', '==', organizationId))

    const key = `serviceCases:recent:${organizationId || 'all'}`

    unsubRecent = subscribeOnce(key, () =>
      onSnapshot(
        query(base, ...clauses),
        snap => {
          const rows = []
          snap.forEach(d => rows.push({ id: d.id, ...d.data() }))
          recentCases.value = rows
          loadingRecent.value = false
        },
        err => {
          console.error('[serviceCasesStore] recent subscribe failed:', err)
          recentCases.value = []
          loadingRecent.value = false
        }
      )
    )
  }

  function unsubscribeAll() {
    if (unsubActive) unsubActive()
    if (unsubRecent) unsubRecent()
    unsubActive = null
    unsubRecent = null
  }

  async function touchCustomerTaskRecalc(customerId) {
    if (!customerId) return
    try {
      await updateDoc(doc(db, 'customers', customerId), {
        taskRecalculationTriggeredAt: serverTimestamp(),
      })
    } catch (e) {
      console.warn('[serviceCasesStore] Failed to trigger customer task recalculation:', e)
    }
  }

  async function createServiceCase(payload) {
    const status = payload.status || 'Open'
    const isActive = ACTIVE_STATUSES.has(status)

    const assignedToUid =
      payload.assignedToUid ||
      (Array.isArray(payload.assignedToUids) && payload.assignedToUids[0]) ||
      null

    const assignedToUids = Array.isArray(payload.assignedToUids)
      ? payload.assignedToUids
      : payload.assignedToUid
        ? [payload.assignedToUid]
        : []

    const docPayload = {
      customerId: payload.customerId,
      organizationId: payload.organizationId || null,

      status,
      waitingOn: status === 'Waiting' ? payload.waitingOn || null : null,
      severity: payload.severity || 'Medium',
      category: payload.category || 'Other',

      title: payload.title || 'Service Case',
      summary: payload.summary || '',

      assignedToUid,
      assignedToUids,

      openedAt: serverTimestamp(),
      openedByUid: payload.openedByUid || null,
      // dhOlReKP data-loss fix: callers always passed this; the payload dropped it.
      openedByEmail: payload.openedByEmail || null,
      lastActivityAt: serverTimestamp(),

      isActive,

      visit: normalizeVisit(payload.visit),
      resolutionSummary: payload.resolutionSummary || null,
      resolvedAt: null,
      closedAt: null,
      closedByUid: null,
    }

    // Provenance + enrichment — set when a case is graduated from a source (Vesta
    // analysis, PowerHub alert, …). Optional + additive; legacy callers unaffected.
    if (payload.source && typeof payload.source === 'object') docPayload.source = payload.source
    if (payload.vestaNotes) docPayload.vestaNotes = payload.vestaNotes
    if (payload.siteContext && typeof payload.siteContext === 'object')
      docPayload.siteContext = payload.siteContext
    if (payload.vestaIssueType) docPayload.vestaIssueType = payload.vestaIssueType
    // service_case_dispatch P3 — the guided modal passes the operator's (edited) crew plan at
    // create time; the enrich trigger skips derivation when a dispatchPlan is already present.
    if (payload.dispatchPlan && typeof payload.dispatchPlan === 'object')
      docPayload.dispatchPlan = requireHomeAccess(payload.dispatchPlan)
    if (payload.dispatchPlanSource) docPayload.dispatchPlanSource = payload.dispatchPlanSource

    const ref = await addDoc(collection(db, 'serviceCases'), docPayload)

    // Trigger immediate workflow recalculation so workflow summary updates quickly
    await touchCustomerTaskRecalc(docPayload.customerId)

    return ref.id
  }

  async function updateServiceCase(id, updates) {
    if (!id) throw new Error('Missing service case id')

    // The update trigger uses these fields for its actor. Close/attachment saves
    // must not inherit a previous editor or trust identity passed by a caller.
    const patch = { ...updates, ...currentActor(), lastActivityAt: serverTimestamp() }
    if (patch.dispatchPlan && typeof patch.dispatchPlan === 'object') {
      patch.dispatchPlan = requireHomeAccess(patch.dispatchPlan)
    }

    // Keep assignee fields consistent
    if (Object.prototype.hasOwnProperty.call(patch, 'assignedToUids')) {
      const arr = Array.isArray(patch.assignedToUids) ? patch.assignedToUids : []
      patch.assignedToUids = arr
      patch.assignedToUid = arr.length ? arr[0] : null
    } else if (Object.prototype.hasOwnProperty.call(patch, 'assignedToUid')) {
      patch.assignedToUids = patch.assignedToUid ? [patch.assignedToUid] : []
    }

    // Status handling
    if (typeof patch.status === 'string') {
      patch.isActive = ACTIVE_STATUSES.has(patch.status)
      if (patch.status !== 'Waiting') patch.waitingOn = null

      if (patch.status === 'Closed' || patch.status === 'Canceled') {
        patch.closedAt = serverTimestamp()
      }
    }

    // Visit normalization
    if (Object.prototype.hasOwnProperty.call(patch, 'visit')) {
      patch.visit = normalizeVisit(patch.visit)
    }

    await updateDoc(doc(db, 'serviceCases', id), patch)

    // Trigger recalc (customerId is usually not sent on update)
    const cid = updates?.customerId || null
    const cached =
      recentCases.value.find(x => x.id === id) || activeCases.value.find(x => x.id === id)
    await touchCustomerTaskRecalc(cid || cached?.customerId)
  }

  async function closeServiceCase(id, { resolutionSummary = '', ignoreAlertInfo = null } = {}) {
    const { updatedByUid: closedByUid } = currentActor()
    // Set both `resolvedAt` and `closedAt`. `createServiceCase` initializes
    // both to null on open; SLA / time-to-resolve metrics read `resolvedAt`,
    // so leaving it null on close (the earlier behavior) made every closed
    // case look unresolved forever. They're synonyms in this code path —
    // close = resolve. If a future flow needs "resolved but not closed",
    // split the setters then.
    const updates = {
      status: 'Closed',
      isActive: false,
      resolutionSummary: resolutionSummary || '',
      closedByUid: closedByUid || null,
      closedAt: serverTimestamp(),
      resolvedAt: serverTimestamp(),
    }

    let suppressionSaved = false
    if (ignoreAlertInfo?.din && ignoreAlertInfo?.name) {
      const result = await changeAlertSuppression({
        din: ignoreAlertInfo.din,
        name: ignoreAlertInfo.name,
        mode: ignoreAlertInfo.snoozeDays == null ? 'ignore' : 'snooze',
        days: ignoreAlertInfo.snoozeDays,
        reason: resolutionSummary || 'Closed via Service Case',
        sourceServiceCaseId: id,
      })
      suppressionSaved = result.changed
      if (!result.auditLogged)
        throw new Error(
          'Alert preference saved, but its activity entry failed. The case remains open; report this audit failure.'
        )
    }
    try {
      await updateServiceCase(id, updates)
    } catch (error) {
      if (suppressionSaved)
        throw new Error(
          'Alert preference saved, but the case could not be closed. Review both before retrying.'
        )
      throw error
    }

    const cached =
      recentCases.value.find(x => x.id === id) || activeCases.value.find(x => x.id === id)
    await touchCustomerTaskRecalc(cached?.customerId)
  }

  return {
    activeCases,
    recentCases,
    loadingActive,
    loadingRecent,

    activeCount,
    activeByCustomerId,

    subscribeActive,
    subscribeRecent,
    unsubscribeAll,

    createServiceCase,
    updateServiceCase,
    closeServiceCase,
  }
})
